import { Button, ButtonProps } from 'antd';
import { colors } from 'config/themes/colors';
import * as React from 'react';
import styled from 'styled-components';

type ExtendedButtonProps = Omit<ButtonProps, 'variant'> & {
  variant?: 'default' | 'blue' | 'skiny';
  iconAlign?: 'left' | 'right';
};

const ExtendedButton = React.forwardRef<HTMLButtonElement, ExtendedButtonProps>((props, ref) => {
  const { variant = 'default', iconAlign, className, ...rest } = props;

  const variantClassName = variant === 'default' ? '' : `${variant}-variant`;

  const shapeClassName = !rest.shape || rest.shape === 'default' ? 'default-shape' : '';

  return (
    <Button ref={ref} {...rest} className={`${className} ${variantClassName} ${shapeClassName}`} />
  );
});

ExtendedButton.displayName = 'ExtendedButton';

const StyledButton = styled(ExtendedButton)`
  &.ant-btn {
    font-feature-settings: normal;
    font-variant-numeric: proportional-nums;
    &.default-shape {
      border-radius: 4px;
    }
    font-size: 14px;
    > span {
      font-size: 14px;
    }

    &.ant-btn-lg {
      font-size: 14px;
    }

    line-height: 1;
    ${({ icon, iconAlign }) =>
      icon
        ? `
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      flex-direction: ${iconAlign === 'right' ? 'row-reverse' : 'row'};
    `
        : ''}
    font-weight: ${({ variant }) => (variant === 'skiny' ? 300 : 500)};

    &.ant-btn-color-default.ant-btn-variant-outlined {
      border: 1px solid ${({ theme }) => theme.primary};
      color: ${({ theme }) => theme.primary};
      background-color: ${({ theme }) => theme.background.primary};

      &.blue-variant {
        border: 1px solid ${({ theme }) => theme.secondary};
        color: ${({ theme }) => theme.secondary};
      }

      &:hover {
        border-color: ${({ theme }) => theme.primary};
        background-color: ${({ theme }) => theme.primary};
        color: #fff;

        &.blue-variant {
          background-color: ${({ theme }) => theme.secondary};
          border-color: ${({ theme }) => theme.secondary};
        }
      }
    }

    &.ant-btn-primary {
      background-color: ${({ theme }) => theme.primary};
      border: 1px solid ${({ theme }) => theme.primary};
      color: #fff;

      &.blue-variant {
        background-color: ${({ theme }) => theme.secondary};
        border: 1px solid ${({ theme }) => theme.secondary};
      }

      &:hover {
        background-color: ${({ theme }) => theme.primary};
        border-width: 1px;

        &.blue-variant {
          background-color: ${({ theme }) => theme.secondary};
          border-width: 1px;
        }
      }
    }

    &.ant-btn-variant-dashed {
      border-color: ${({ theme }) => theme.primary};
      color: ${({ theme }) => theme.primary};
      background-color: ${({ theme }) => theme.background.primary};

      &.blue-variant {
        border-color: ${({ theme }) => theme.secondary};
        color: ${({ theme }) => theme.secondary};
        background: ${({ theme }) =>
          theme.name === 'atsDark' ? theme.background.primary : colors.blue[25]};
      }
    }

    &.ant-btn-text {
      color: ${({ theme }) => theme.primary};
      border-color: transparent;

      &.blue-variant {
        color: ${({ theme }) => theme.secondary};
      }

      &:hover {
        color: ${({ theme }) => theme.primary};
        border-color: transparent;
      }

      &.ant-btn-text[disabled] {
        border-color: transparent;
      }
    }

    &.ant-btn-variant-link {
      color: ${({ theme }) => theme.primary};
      border-width: 0 !important;

      &.blue-variant {
        color: ${({ theme }) => theme.secondary};
      }

      &:hover {
        text-decoration: underline;
        color: ${({ theme }) => theme.primary};
        background: transparent;
        border-width: 0 !important;
      }

      &.blue-variant:hover {
        color: ${({ theme }) => theme.secondary};
      }

      &.ant-btn-variant-link[disabled] {
        border-color: transparent;
        background: transparent;

        &:hover {
          text-decoration: none;
        }
      }
    }

    &.ant-btn-background-ghost {
      background-color: transparent;
    }

    &.ant-btn-color-dangerous {
      color: #ff4d4f;
      border-color: #ff4d4f;
      background: #fff;

      &:hover {
        background-color: #ff4d4f;
      }

      &.ant-btn-primary {
        background-color: #ff4d4f;
        border-color: #ff4d4f;
        color: #fff;

        &:hover {
          background-color: #ff4d4feb;
          border-color: #ff4d4feb;
        }
      }
    }

    &.ant-btn.disabled,
    &.ant-btn[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const NewButton = StyledButton as unknown as typeof ExtendedButton;

export { NewButton };
